// src/MarkdownPost.js
import { useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeHighlight from "rehype-highlight";
import "highlight.js/styles/stackoverflow-light.css"; // Light mode

import ImageLightbox from "./ImageLightbox";

export default function MarkdownPost({ src, onHeadingsExtracted }) {
    const [text, setText] = useState("");

    useEffect(() => {
        let cancelled = false;
        fetch(src)
            .then((r) => r.text())
            .then((t) => {
                if (!cancelled) {
                    setText(t);
                    // Extract headings for TOC
                    if (onHeadingsExtracted) {
                        const headings = extractHeadings(t);
                        onHeadingsExtracted(headings);
                    }
                }
            })
            .catch(() => {
                if (!cancelled) setText("# Oops\nCould not load post.");
            });
        return () => { cancelled = true; };
    }, [src, onHeadingsExtracted]);

    return (
        <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            rehypePlugins={[rehypeHighlight]}
            components={{
                // Add IDs to headings for anchor links
                h2: ({ children, ...props }) => <h2 id={slugify(children)} {...props}>{children}</h2>,
                h3: ({ children, ...props }) => <h3 id={slugify(children)} {...props}>{children}</h3>,
                h4: ({ children, ...props }) => <h4 id={slugify(children)} {...props}>{children}</h4>,
                a: (props) => {
                    const { children, href, ...rest } = props;
                    const hasChildren = Array.isArray(children) ? children.some(Boolean) : Boolean(children);
                    const fallbackLabel = typeof href === "string" ? href.replace(/^https?:\/\//, "") : "link";
                    const aProps = {
                        ...rest,
                        href,
                        target: "_blank",
                        rel: "noopener noreferrer",
                        ...(hasChildren || rest["aria-label"] ? {} : { "aria-label": fallbackLabel }),
                    };
                    return <a {...aProps}>{hasChildren ? children : null}</a>;
                },
                img: (props) => {
                    const { alt = "", ...rest } = props;
                    return <ImageLightbox {...rest} alt={alt} />;
                },
            }}
        >
            {text}
        </ReactMarkdown>
    );
}

// Helper to extract headings from markdown
function extractHeadings(markdown) {
    const headings = [];
    const lines = markdown.split('\n');

    for (const line of lines) {
        const match = line.match(/^(#{2,4})\s+(.+)$/);
        if (match) {
            const level = match[1].length;
            const text = match[2];
            headings.push({ level, text, id: slugify(text) });
        }
    }

    return headings;
}

// Convert heading text to URL-friendly ID
function slugify(children) {
    if (typeof children === 'string') {
        return children.toLowerCase().replace(/[^\w\s-]/g, '').replace(/\s+/g, '-');
    }
    if (Array.isArray(children)) {
        return children.map(c => typeof c === 'string' ? c : '').join('').toLowerCase().replace(/[^\w\s-]/g, '').replace(/\s+/g, '-');
    }
    return '';
}